/**
 * Page Tracking Service for comprehensive user journey tracking
 *
 * Features:
 * - Track page views with rich metadata
 * - Track navigation chains
 * - Session-based tracking with persistence
 * - Integration with click tracking
 * - Automatic page exit tracking
 */

import { trackEvent } from "logger/logger";
import { InsightProps } from "logger/insights";
import { sessionManager } from "logger/session-manager";
import { clickTrackingService, ClickData } from "logger/click-tracking";

/**
 * Analytics tracking system version
 * Increment when making breaking changes to tracking implementation
 */
export const TRACKING_VERSION = "1.0.1";

/**
 * Valid page names for tracking
 * Add new pages here to maintain type safety across the application
 *
 * Organized by flow type for maintainability
 */
export type PageName =
    // Booking Flow Pages
    | "SelectGuests"
    | "SelectChildren"
    | "SelectDate"
    | "SelectTime"
    | "SelectActivity"
    | "SelectActivityTime"
    | "AvailabilityActivityBooking"
    | "BookingPreorderView"
    | "BookingSummary"
    | "DetailsWithPaymentForm"
    | "PaymentConfirmation"
    | "TwoFactorConfirmation"
    | "MyBooking"
    // Booking Request Flow
    | "BookingRequestWizard"
    | "BookingRequestGuests"
    | "BookingRequestChildren"
    | "BookingRequestDate"
    // Edit Booking Flow
    | "EditBookingModal"
    // Error Pages
    | "ReservationFailedError"
    | "PaymentError"
    | "SomethingWrongError"
    | "BrokenLinkError"
    | "ExpiredLinkError"
    | "ServerDidNotRespondError"
    // Experience View
    | "ExperienceView";

/**
 * Booking flow classification
 * Identifies which overall journey the user is on
 */
export enum BookingFlow {
    /** Standard booking flow */
    Simple = "simple",
    /** Activity booking flow with activity selection */
    Activity = "activity",
    /** View/manage existing booking */
    MyBooking = "mybooking",
}

/**
 * Feature modes within booking flows
 */
export enum BookingFeature {
    /** Normal booking flow */
    Default = "default",
    /** Booking request when unavailable */
    Request = "request",
    /** Edit existing booking */
    Edit = "edit",
}

/**
 * Page type classification
 */
export enum PageType {
    WizardStep = "wizard-step",
    Page = "page",
    Modal = "modal",
}

/**
 * Exit reasons for page tracking
 */
enum PageExitReason {
    Navigation = "navigation",
    Back = "back",
    Close = "close",
    Reload = "reload",
    Error = "error",
    Timeout = "timeout",
}

/**
 * Rich metadata for page tracking
 */
export interface PageMetadata {
    // Booking flow classification
    bookingFlow?: BookingFlow;

    // Booking flow state
    guests?: number;
    children?: number;
    date?: string;
    selectedTime?: string;
    times?: string[];
    isWaitList?: boolean;

    // Context
    feature?: BookingFeature;
    system?: string;
    unitId?: string;

    // Activity-specific
    activityId?: string;
    activityName?: string;

    // Normal flow activity specific
    activityIds?: string[];
    activityNames?: string[];

    // Page-specific custom metadata
    [key: string]: string | number | boolean | string[] | undefined;
}

/**
 * Current page context for click tracking and analytics
 */
export interface PageContext {
    sessionId: string;
    currentPage: PageName | null;
    pageType: PageType | null;
    navigationChain: string[]; // Keep as string[] - stored in sessionStorage
    navigationDepth: number;
    bookingState: Partial<PageMetadata>;
}

/**
 * Page view tracking data
 */
interface PageViewData {
    pageName: PageName;
    pageType: PageType;
    startTime: number;
    metadata: PageMetadata;
}

class PageTrackingService {
    private static instance: PageTrackingService;

    // Current page state (in-memory for performance)
    private currentPageView: PageViewData | null = null;

    // Track if we're navigating within the app (vs external navigation)
    private isInternalNavigation = false;

    private constructor() {
        // Setup cleanup on page unload
        if (typeof window !== "undefined") {
            window.addEventListener("beforeunload", () => {
                this.handlePageUnload();
            });

            // Detect browser back/forward navigation
            window.addEventListener("popstate", () => {
                this.handleBackNavigation();
            });

            // Check if this page load was a reload
            this.checkIfReloadOnLoad();
        }
    }

    static getInstance(): PageTrackingService {
        if (!PageTrackingService.instance) {
            PageTrackingService.instance = new PageTrackingService();
        }
        return PageTrackingService.instance;
    }

    /**
     * Track a page view with metadata
     */
    trackPageView(
        pageName: PageName,
        pageType: PageType,
        metadata: Partial<PageMetadata> = {}
    ): void {
        // Mark that we're doing internal navigation
        this.isInternalNavigation = true;

        // Exit previous page if exists
        if (this.currentPageView) {
            this.trackPageExit(PageExitReason.Navigation, pageName);
        }

        // Create new page view
        const startTime = Date.now();
        this.currentPageView = {
            pageName,
            pageType,
            startTime,
            metadata: metadata,
        };

        // Add to navigation chain
        sessionManager.addToNavigationChain(pageName);

        // Reset click tracking for new page
        clickTrackingService.resetPageStartTime();

        // Track the view event
        this.sendPageViewEvent();

        // Reset internal navigation flag after a brief delay
        setTimeout(() => {
            this.isInternalNavigation = false;
        }, 100);
    }

    /**
     * Track page exit
     */
    trackPageExit(
        reason: PageExitReason = PageExitReason.Navigation,
        destination?: PageName
    ): void {
        if (!this.currentPageView) return;

        const duration = Date.now() - this.currentPageView.startTime;

        // Get click data from click tracking service
        const clickData: ClickData = clickTrackingService.getClicksForPage();

        // Send exit event
        const exitProps: InsightProps = {
            pageName: this.currentPageView.pageName,
            pageType: this.currentPageView.pageType,
            sessionId: sessionManager.getSessionId(),
            exitReason: reason,
            durationMs: duration,
            durationSeconds: Math.round(duration / 1000),
            trackingVersion: TRACKING_VERSION,
            clicks: clickData.clicks,
            clickCount: clickData.clickCount,
        };

        if (destination) {
            exitProps.exitDestination = destination;
        }

        trackEvent(`Page.${this.currentPageView.pageName}.Exited`, exitProps);

        // Also send time spent event
        this.sendTimeSpentEvent(duration);
    }

    /**
     * Get current page context for click tracking
     */
    getCurrentContext(): PageContext {
        return {
            sessionId: sessionManager.getSessionId(),
            currentPage: this.currentPageView?.pageName || null,
            pageType: this.currentPageView?.pageType || null,
            navigationChain: sessionManager.getNavigationChain(),
            navigationDepth: sessionManager.getNavigationChain().length,
            bookingState: this.currentPageView?.metadata || {},
        };
    }

    /**
     * Get current page name
     */
    getCurrentPage(): string | null {
        return this.currentPageView?.pageName || null;
    }

    /**
     * Reset session (e.g., after booking completion)
     */
    resetSession(): void {
        if (this.currentPageView) {
            this.isInternalNavigation = true; // Prevent duplicate tracking
            this.trackPageExit(PageExitReason.Close);
            this.isInternalNavigation = false;
        }

        this.currentPageView = null;
        sessionManager.resetSession();
    }

    /**
     * Track error-driven page exit
     * Call this when an error forces the user away from the current page
     */
    trackErrorExit(): void {
        if (this.currentPageView) {
            this.trackPageExit(PageExitReason.Error);
        }
    }

    /**
     * Send page view event to Application Insights
     */
    private sendPageViewEvent(): void {
        if (!this.currentPageView) return;

        const { pageName, pageType, metadata } = this.currentPageView;
        const navigationChain = sessionManager.getNavigationChain();

        const eventProps: InsightProps = {
            pageName,
            pageType,
            sessionId: sessionManager.getSessionId(),
            sessionStartTime: sessionManager.getSessionStartTime(),
            sessionAge: sessionManager.getSessionAge(),
            navigationChain: navigationChain.join(" → "),
            navigationDepth: navigationChain.length,
            trackingVersion: TRACKING_VERSION,
            ...this.sanitizeMetadata(metadata),
        };

        // Add previousPage if it exists
        const previousPage = navigationChain[navigationChain.length - 2];
        if (previousPage) {
            eventProps.previousPage = previousPage;
        }

        trackEvent(`Page.${pageName}.Viewed`, eventProps);
    }

    /**
     * Send time spent event
     */
    private sendTimeSpentEvent(duration: number): void {
        if (!this.currentPageView) return;

        const eventProps: InsightProps = {
            pageName: this.currentPageView.pageName,
            pageType: this.currentPageView.pageType,
            sessionId: sessionManager.getSessionId(),
            durationMs: duration,
            durationSeconds: Math.round(duration / 1000),
            trackingVersion: TRACKING_VERSION,
        };

        trackEvent(`Page.${this.currentPageView.pageName}.TimeSpent`, eventProps);
    }

    /**
     * Handle browser back/forward navigation
     */
    private handleBackNavigation(): void {
        if (this.currentPageView && !this.isInternalNavigation) {
            this.trackPageExit(PageExitReason.Back);
        }
    }

    /**
     * Check if the current page load was a reload and track previous session exit
     * This runs once when the tracking service initializes
     */
    private checkIfReloadOnLoad(): void {
        if (typeof window === "undefined" || !window.performance) return;

        // Check navigation type using Performance Navigation Timing API
        const perfData = window.performance.getEntriesByType(
            "navigation"
        )[0] as PerformanceNavigationTiming;

        let isReload = false;

        if (perfData) {
            isReload = perfData.type === "reload";
        }

        if (isReload) {
            // Track that the previous session ended with a reload
            // We'll send this event for the previous page that was reloaded
            const previousPage = sessionStorage.getItem("lastTrackedPage");
            const previousPageType = sessionStorage.getItem("lastTrackedPageType");
            const sessionId = sessionManager.getSessionId();

            if (previousPage) {
                trackEvent(`Page.${previousPage}.Exited`, {
                    pageName: previousPage,
                    pageType: previousPageType || PageType.Page,
                    sessionId: sessionId,
                    exitReason: PageExitReason.Reload,
                    durationMs: 0, // We don't have accurate duration after reload
                    durationSeconds: 0,
                    trackingVersion: TRACKING_VERSION,
                    clickCount: 0,
                    clicks: [],
                    wasReconstructed: true, // Flag to indicate this was sent after reload
                });
            }

            // Clear the stored page data
            sessionStorage.removeItem("lastTrackedPage");
            sessionStorage.removeItem("lastTrackedPageType");
        }
    }

    /**
     * Handle page unload
     */
    private handlePageUnload(): void {
        if (this.currentPageView && !this.isInternalNavigation) {
            // Store current page info for reload detection
            sessionStorage.setItem("lastTrackedPage", this.currentPageView.pageName);
            sessionStorage.setItem("lastTrackedPageType", this.currentPageView.pageType);

            // Track the exit
            this.trackPageExit(PageExitReason.Close);
        }
    }

    /**
     * Sanitize metadata to ensure all values are valid for Application Insights
     */
    private sanitizeMetadata(metadata: PageMetadata): InsightProps {
        const sanitized: InsightProps = {};

        Object.entries(metadata).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                // Convert arrays to comma-separated strings
                if (Array.isArray(value)) {
                    sanitized[key] = value.join(", ");
                } else {
                    sanitized[key] = value;
                }
            }
        });

        return sanitized;
    }
}

// Export singleton instance
export const pageTrackingService = PageTrackingService.getInstance();
